import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react"
import { useField } from "formik"
import { makeStyles } from "tss-react/mui"
import { Grid } from "@mui/material"
import { MainOrderFormikRef, MeetingServiceTabValues } from "../components/form/formTypes"
import {
    SelectedMainUserState,
    SelectedPriceListItem,
    CreateOrderDialogFlow,
    CreateOrderDialogType,
    ResourceItem,
} from "../createOrderTypes"
import { MeetingServiceOrder } from "../../../../modules/merchant/accounting/accounting/accountingTypes"
import { OutlookBookingSuccessArgs } from "../../../../modules/order/pages/outlookCreateOrderTypes"
import { LocalizedStrict } from "../../../../modules/localization/components/AppLocalized"
import { NullableCalendarEvent } from "../components/resource/calendar/fullCalendarTypes"
import { ButtonConfigType } from "../../filters/orderTypeFilter/StandardOrderTypeFilter"
import { CreateOrderDialogTitleWrap } from "../components/CreateOrderDialogTitleWrap"
import { useInitialEventState } from "../components/resource/useInitialEventState"
import { SelectPriceLists } from "../components/shop/priceLists/SelectPriceLists"
import { CREATE_ORDER_TOP_BAR_HEIGHT } from "../components/CreateOrderDialogTitle"
import { DRAWER_WIDTH } from "../components/drawer/CreateOrderDialogDrawer"
import { useConfirmAbandonDialog } from "../hooks/useConfirmAbandonDialog"
import { AbandonBookingDialog } from "../components/AbandonBookingDialog"
import { ConfigErrorBoundary } from "../components/ConfigErrorBoundary"
import { AnimatedTabWrapper } from "../components/AnimatedTabWrapper"
import { useSelectPriceLists } from "../hooks/useSelectPriceLists"
import { OrderContextState, OrderFormStatus } from "./helpers"
import { CreateOrderType, useOrderCreation } from "../helpers"
import { TabContent } from "./TabContent"

type StyleProps = Readonly<{
    isDrawerOpen: boolean
}>

const useStyles = makeStyles<StyleProps>()((_theme, { isDrawerOpen }) => ({
    dialogContent: {
        display: "flex",
        flexDirection: "column",
        position: "relative",
        zIndex: 1,
        width: isDrawerOpen ? `calc(100% - ${DRAWER_WIDTH})` : "100%",
        transition: "all 0.3s ease-in-out",
        minHeight: `calc(100vh - ${CREATE_ORDER_TOP_BAR_HEIGHT})`,
    },
    content: {
        flexGrow: 1,
        position: "relative",
        transition: "all 0.3s ease-in-out",
    },
}))

type OrderFormProps = Readonly<{
    user: SelectedMainUserState
    filteredTabButtonConfig: ButtonConfigType<CreateOrderType>[]
    initialOrderType: CreateOrderType
    newOrderType: CreateOrderType | null
    dialogType: CreateOrderDialogType
    handleClose: () => void
    orderContext: OrderContextState
    orderDetails?: MeetingServiceOrder
    flowConfig?: CreateOrderDialogFlow
    setOrderContext: Dispatch<SetStateAction<OrderContextState>>
    orderFormStatusRef: React.MutableRefObject<OrderFormStatus>
    handleSubmitMeetingService: (values: MeetingServiceTabValues) => void
    outerFormik: MainOrderFormikRef
    handleOutlookBookingCompleted?: (args: OutlookBookingSuccessArgs) => void
}>

export function OrderForm({
    user,
    filteredTabButtonConfig,
    initialOrderType,
    newOrderType,
    dialogType,
    handleClose,
    orderContext,
    orderDetails,
    flowConfig,
    setOrderContext,
    orderFormStatusRef,
    handleSubmitMeetingService,
    outerFormik,
    handleOutlookBookingCompleted,
}: OrderFormProps) {
    const { orderType, setOrderType } = useOrderCreation(initialOrderType)
    const isRefundOrder = dialogType === CreateOrderDialogType.CREDIT_NOTE

    const [{ value: event }, , eventHelpers] = useField<NullableCalendarEvent>("event")
    const [, , fullResourcesHelpers] = useField<ResourceItem[]>("fullResourcesList")
    const [{ value: priceList }] = useField<SelectedPriceListItem | null>("priceList")

    const [isDrawerOpen, setIsDrawerOpen] = useState(orderType === CreateOrderType.MEETING_SERVICE)
    const [confirmBookingDialog, setConfirmBookingDialog] = useState<boolean>(false)

    const handleChangeLocalEvent = (nextEvent: NullableCalendarEvent) => {
        eventHelpers.setValue(nextEvent)
        if (!nextEvent) {
            fullResourcesHelpers.setValue([])
        }
    }

    const handleOpenMeetingServiceTab = () => {
        setIsDrawerOpen(true)
        setOrderType(CreateOrderType.MEETING_SERVICE)
    }
    const { showSelectPriceListsDialog, setShowSelectPriceListsDialog, handleSelectPriceList } =
        useSelectPriceLists(handleOpenMeetingServiceTab)

    const { confirmAbandonDialogState, handleOpenConfirmAbandonDialog, handleCloseConfirmAbandonDialog } =
        useConfirmAbandonDialog()

    const { classes } = useStyles({ isDrawerOpen })

    const { initialEventState, setInitialEventState, handleCancelClick } = useInitialEventState(
        event,
        handleChangeLocalEvent,
        orderDetails,
        flowConfig?.flow,
    )

    useEffect(() => {
        if (orderType === CreateOrderType.MEETING_SERVICE && !priceList) {
            handleOpenSelectPriceListsDialog()
        }
    }, [orderType, priceList])

    useEffect(() => {
        if (newOrderType && newOrderType !== orderType) {
            handleChangeOrderType(newOrderType)
        }
    }, [orderType, newOrderType])

    const handleChangeOrderType = useCallback(
        (type: CreateOrderType) => {
            if (dialogType !== CreateOrderDialogType.ORDER) return

            if (type === CreateOrderType.RESOURCE_BOOKING) {
                setIsDrawerOpen(false)
                setOrderType(type)
                return
            }

            if (type === CreateOrderType.MEETING_SERVICE) {
                if (event && (!initialEventState.initValues?.id || initialEventState.isUpdated)) {
                    handleOpenConfirmAbandonDialog()
                } else if (priceList) {
                    handleOpenMeetingServiceTab()
                } else {
                    handleOpenSelectPriceListsDialog()
                }
            }
        },
        [event, initialEventState.isUpdated, initialEventState.initValues?.id, priceList],
    )

    const handleOpenSelectPriceListsDialog = useCallback(() => {
        if (isRefundOrder) {
            return
        }
        setShowSelectPriceListsDialog(true)
    }, [setShowSelectPriceListsDialog, isRefundOrder])

    const tabs = TabContent({
        confirmBookingDialog,
        setConfirmBookingDialog,
        initialEventState,
        setInitialEventState,
        handleChangeOrderType,
        handleCancelClick,
        handleClose,
        user,
        dialogType,
        orderContext,
        flowConfig,
        setOrderContext,
        orderFormStatusRef,
        handleOpenSelectPriceListsDialog,
        orderType,
        isDrawerOpen,
        orderDetails,
        handleSubmitMeetingService,
        outerFormik,
        handleOutlookBookingCompleted,
    })

    // Abandon Dialog
    const hasResources = !!event && event.resourceIds.length > 0
    const handleConfirmAbandonDialogClick = () => {
        handleCloseConfirmAbandonDialog()
        setConfirmBookingDialog(true)
    }
    const handleCancelAbandonDialogClick = () => {
        handleCancelClick()
        handleCloseConfirmAbandonDialog()
        if (priceList) {
            handleOpenMeetingServiceTab()
        } else {
            handleOpenSelectPriceListsDialog()
        }
    }
    // END Abandon Dialog

    return (
        <>
            <CreateOrderDialogTitleWrap
                handleClose={handleClose}
                orderType={orderType}
                handleChangeOrderType={handleChangeOrderType}
                filteredTabButtonConfig={filteredTabButtonConfig}
                dialogType={dialogType}
                initialEventState={initialEventState}
                handleCancelClick={handleCancelClick}
                confirmBookingDialog={confirmBookingDialog}
                setConfirmBookingDialog={setConfirmBookingDialog}
                handleOpenSelectPriceListsDialog={handleOpenSelectPriceListsDialog}
                flowConfig={flowConfig}
                orderContext={orderContext}
            />
            <ConfigErrorBoundary onClose={handleClose} flowConfig={flowConfig}>
                <Grid container flexWrap="nowrap">
                    <Grid container className={classes.dialogContent}>
                        <Grid container className={classes.content}>
                            <AnimatedTabWrapper activeKey={orderType} tabs={tabs} animationDuration={0.5} />
                        </Grid>
                    </Grid>

                    {confirmAbandonDialogState && (
                        <LocalizedStrict
                            id={
                                hasResources
                                    ? "create-order-confirm-booking-confirm-dialog"
                                    : "create-order-confirm-booking-confirm-no-resources-dialog"
                            }
                            attrs={{ title: true, message: true, buttonLabelSubmit: true, buttonLabelCancel: true }}
                        >
                            <AbandonBookingDialog
                                title={hasResources ? "Confirm booking" : "Booking not complete"}
                                message={
                                    hasResources
                                        ? "Your booking has not been completed. Please confirm your booking before proceeding."
                                        : "You haven't selected any resources yet. If you leave now, your booking will not be completed."
                                }
                                handleSubmitClick={
                                    hasResources ? handleConfirmAbandonDialogClick : handleCloseConfirmAbandonDialog
                                }
                                handleCancelClick={handleCancelAbandonDialogClick}
                                buttonLabelSubmit={hasResources ? "Confirm" : "Select resources"}
                                buttonLabelCancel={hasResources ? "Cancel" : "Leave anyway"}
                                open={confirmAbandonDialogState}
                                onClose={handleCloseConfirmAbandonDialog}
                            />
                        </LocalizedStrict>
                    )}
                </Grid>
            </ConfigErrorBoundary>

            {!isRefundOrder && showSelectPriceListsDialog && (
                <SelectPriceLists
                    userId={user.userId}
                    onClose={() => {
                        if (initialOrderType === CreateOrderType.MEETING_SERVICE && !priceList) {
                            return
                        } else setShowSelectPriceListsDialog(false)
                    }}
                    onSelect={handleSelectPriceList}
                    handleCloseMainDialog={handleClose}
                    mode={orderContext.mode}
                />
            )}
        </>
    )
}
